import React, { useState, useRef, useEffect } from 'react';
import { Scale } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface LawCitation {
  /** Short identifier shown in the popover header (e.g. "FDL 33/2021 — Art. 25"). */
  code: string;
  /** Plain-language explanation of the rule. */
  description: string;
  /** Optional external URL (kept inert by default; rendered as a non-link if absent). */
  url?: string;
}

interface LawReferenceProps {
  /** A single citation, or a list when one widget covers multiple statutes. */
  citations: LawCitation[];
  /** Optional aria-label override; defaults to translation key. */
  ariaLabel?: string;
}

/**
 * Tiny scale-of-justice icon button. On hover/focus it shows a popover listing
 * the relevant UAE law references for the surrounding metric. Designed to add
 * legal context without bloating the UI.
 */
export const LawReference: React.FC<LawReferenceProps> = ({ citations, ariaLabel }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={ariaLabel ?? t('payroll.complianceCenter.lawRefAria', 'Show law references')}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
        title={t('payroll.complianceCenter.lawRefTitle', 'Legal references')}
      >
        <Scale className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          role="dialog"
          className="absolute z-30 end-0 mt-1 w-72 max-w-[80vw] rounded-lg border border-border bg-white shadow-lg p-3 text-start"
        >
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            {t('payroll.complianceCenter.lawRefHeader', 'Legal Basis')}
          </p>
          <ul className="space-y-2">
            {citations.map((c, i) => (
              <li key={i} className="text-xs">
                <p className="font-semibold text-blue-700">{c.code}</p>
                <p className="text-muted-foreground mt-0.5 leading-relaxed">{c.description}</p>
                {c.url && (
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline mt-0.5 inline-block"
                  >
                    {t('common.readMore', 'Read more →')}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
